import React, { useCallback, useEffect, useMemo, useState } from 'react'
import Swal from 'sweetalert2'
import { useNavigate } from 'react-router-dom'
import {
  claimCashbackSettlement,
  getCashbackHistory,
  getCashbackMe,
  type MemberCashbackSettlement,
  type MemberCashbackSummary,
} from '../services/cashbackService'
import {
  getCashbackDisplayPromotionConfig,
  type UserCashbackDisplaySettings,
} from '../services/promotionConfigService'
import { useWallet } from '../contexts/WalletContext'

const defaultDisplay: UserCashbackDisplaySettings = {
  pageTitle: 'รับเครดิตเงินคืน',
  pageSubtitle: 'ตรวจสอบยอดคืนล่าสุด กดรับ cashback ได้ทันที และติดตามประวัติการรับเงินคืนได้ในหน้าเดียว',
  heroBadgeText: 'Cashback Center',
  withdrawalButtonText: 'ไปหน้าแจ้งถอน',
  noticeText: 'รายได้การคืนยอดเล่นของท่านสามารถแจ้งถอนได้ทุกเวลา หากมียอดรายได้มากกว่า 15 บาทขึ้นไป',
  minWithdrawalAmount: 15,
  cashbackRateText: '0.3% - 0.8%',
  promotionalMessage: 'สุดยอดโปรโมชั่น รับเงินคืนฟรี ทันทีทุกวัน ทั้งวัน ไม่อั้น ไม่ติดเทิร์น ง่ายๆ',
  disabledTitle: 'ปิดใช้งานชั่วคราว',
  disabledMessage: 'ระบบ cashback ปิดใช้งานในขณะนี้ กรุณาติดต่อแอดมินหากมีคำถาม',
  historyEmptyText: 'ยังไม่มีประวัติ cashback',
  showRateTable: true,
  showExample: true,
  promotionRoute: '/cashback',
  promotionCard: {
    imageUrl: '',
    imageAlt: 'คืนยอดเสีย',
    badgeText: 'Cashback',
    title: 'คืนยอดเสีย',
    description: 'ดูยอด cashback ล่าสุด กดรับสิทธิ์ และดูประวัติการรับเงินคืน',
    buttonText: 'ไปหน้า Cashback',
  },
}

const EMPTY_SUMMARY: MemberCashbackSummary = {
  claimableAmount: 0,
  pendingAmount: 0,
  approvedAmount: 0,
  paidAmount: 0,
  totalEarned: 0,
}

const formatDateTime = (value?: string | null) => {
  if (!value) return '-'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '-'
  return date.toLocaleString('th-TH', {
    dateStyle: 'medium',
    timeStyle: 'short',
  })
}

const statusLabel: Record<string, string> = {
  claimable: 'รอรับสิทธิ์',
  pending: 'รออนุมัติ',
  approved: 'อนุมัติแล้ว',
  rejected: 'ถูกปฏิเสธ',
  paid: 'จ่ายแล้ว',
  payment_failed: 'จ่ายไม่สำเร็จ',
  expired: 'หมดอายุ',
}

const statusClass: Record<string, string> = {
  claimable: 'c-green',
  pending: 'c-red',
  approved: 'c-main',
  rejected: 'c-red',
  paid: 'c-green',
  payment_failed: 'c-red',
  expired: 'c-dark',
}

const CashbackPage: React.FC = () => {
  const navigate = useNavigate()
  const { formatCurrency, refreshWallet } = useWallet()
  const [displayConfig, setDisplayConfig] = useState<UserCashbackDisplaySettings>(defaultDisplay)
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [claiming, setClaiming] = useState(false)
  const [cashbackDisabled, setCashbackDisabled] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [claimMessage, setClaimMessage] = useState('')
  const [summary, setSummary] = useState<MemberCashbackSummary>(EMPTY_SUMMARY)
  const [latestClaimable, setLatestClaimable] = useState<MemberCashbackSettlement | null>(null)
  const [history, setHistory] = useState<MemberCashbackSettlement[]>([])

  const loadDisplayConfig = useCallback(async () => {
    try {
      const response = await getCashbackDisplayPromotionConfig()
      const nextSettings = response?.data?.settings
      if (response?.success && nextSettings) {
        setDisplayConfig((prev) => ({ ...prev, ...nextSettings }))
      } else {
        setDisplayConfig(defaultDisplay)
      }
    } catch {
      setDisplayConfig(defaultDisplay)
    }
  }, [])

  const loadCashbackData = useCallback(async (options?: { silent?: boolean }) => {
    if (!options?.silent) setLoading(true)
    setError(null)

    try {
      const [meRes, historyRes] = await Promise.all([getCashbackMe(), getCashbackHistory(10)])
      setCashbackDisabled(false)
      if (meRes?.success) {
        setSummary(meRes.data.summary || EMPTY_SUMMARY)
        setLatestClaimable(meRes.data.latestClaimable || null)
      } else {
        setSummary(EMPTY_SUMMARY)
        setLatestClaimable(null)
      }
      setHistory(historyRes?.success && Array.isArray(historyRes.data) ? historyRes.data : [])
    } catch (err) {
      const errorLike = err as { message?: string; status?: number }
      const isDisabled =
        errorLike?.status === 404 ||
        String(errorLike?.message || '').toLowerCase().includes('not enabled')

      if (isDisabled) {
        setCashbackDisabled(true)
        setSummary(EMPTY_SUMMARY)
        setLatestClaimable(null)
        setHistory([])
      } else if (String(errorLike?.message || '') !== 'AUTH_EXPIRED') {
        setError(err instanceof Error ? err.message : 'ไม่สามารถโหลดข้อมูล cashback ได้')
      }
    } finally {
      if (!options?.silent) setLoading(false)
      setRefreshing(false)
    }
  }, [])

  useEffect(() => {
    void (async () => {
      await loadDisplayConfig()
      await loadCashbackData()
    })()
  }, [loadCashbackData, loadDisplayConfig])

  const cards = useMemo(() => ([
    { label: 'รอรับ', value: summary.claimableAmount, className: 'c-green' },
    { label: 'รออนุมัติ', value: summary.pendingAmount, className: 'c-red' },
    { label: 'จ่ายแล้ว', value: summary.paidAmount, className: 'c-main' },
    { label: 'รวมทั้งหมด', value: summary.totalEarned, className: 'c-red' },
  ]), [summary])

  const tableRows = useMemo(() => ([
    { label: 'ประเภทโบนัส', value: 'คืนยอดเล่นรายวัน' },
    { label: '% โบนัส จากยอดเล่น', value: displayConfig.cashbackRateText || '-' },
    { label: 'โบนัสที่สามารถขอได้', value: 'ตามยอดเสียสุทธิที่เข้าเงื่อนไข' },
    { label: 'ถอนได้สูงสุด', value: 'ขึ้นอยู่กับค่า config ของระบบ' },
    { label: 'เทิร์นกี่เท่าจากยอดเล่น', value: `${latestClaimable?.bonusLock?.turnoverMultiplier || 1}x` },
    { label: 'จำนวนเทิร์นที่จะติด', value: latestClaimable ? 'แสดงตามงวดที่พร้อมรับล่าสุด' : 'รอรายการพร้อมรับ' },
  ]), [displayConfig.cashbackRateText, latestClaimable])

  const exampleSteps = useMemo(() => ([
    'เล่นบาคาร่าสด',
    'แทง Player 1,000,000 บาท ผลออกมาว่า Player ชนะ',
    'แทง Player อีกครั้ง 1,000,000 บาท ผลออกมาว่า Banker ชนะ',
    'รวมยอดเดิมพันทั้ง 2 ครั้ง คือ 2,000,000 บาท',
    `ท่านจะได้รับเงินคืน ${displayConfig.cashbackRateText}`,
    'รายได้ที่จะได้รับขึ้นอยู่กับเรทที่ tenant ตั้งค่าไว้',
    'ไม่ว่าท่านจะเล่นแพ้หรือชนะ ก็ได้รับเงินคืนได้',
  ]), [displayConfig.cashbackRateText])

  const handleRefresh = async () => {
    setRefreshing(true)
    setClaimMessage('')
    await loadCashbackData({ silent: true })
  }

  const handleClaim = async () => {
    if (!latestClaimable?._id || claiming || cashbackDisabled) return

    const confirmed = await Swal.fire({
      icon: 'question',
      title: 'ยืนยันการรับเครดิตเงินคืน',
      html: `คุณต้องการรับเครดิตเงินคืนจำนวน <strong>${formatCurrency(latestClaimable.calculation?.cashbackAmount || 0)}</strong> ใช่หรือไม่`,
      showCancelButton: true,
      confirmButtonText: 'ยืนยัน',
      cancelButtonText: 'ยกเลิก',
      confirmButtonColor: '#198754',
      cancelButtonColor: '#dc3545',
    })

    if (!confirmed.isConfirmed) return

    try {
      setClaiming(true)
      setClaimMessage('')
      const response = await claimCashbackSettlement(latestClaimable._id)
      await Promise.all([loadCashbackData({ silent: true }), refreshWallet()])
      setClaimMessage(response.message || 'ส่งคำขอรับ cashback สำเร็จแล้ว')
      await Swal.fire({
        icon: 'success',
        title: response.message || 'ส่งคำขอรับ cashback สำเร็จแล้ว',
        confirmButtonText: 'ตกลง',
      })
    } catch (err) {
      if (String((err as Error)?.message || '') !== 'AUTH_EXPIRED') {
        await Swal.fire({
          icon: 'error',
          title: 'ไม่สามารถรับ cashback ได้',
          text: err instanceof Error ? err.message : 'เกิดข้อผิดพลาด',
          confirmButtonText: 'ตกลง',
        })
      }
    } finally {
      setClaiming(false)
    }
  }

  return (
    <div className="content pb-3">
      <div className="container">
        <div className="row px--1">
          <div className="col-12 px-1">
            <div className="bdr-5 bx-sd mb-2">
              <div className="row mx--1">
                <div className="col-12 px-1">
                  <div className="w-100 p-2 bgc-4 wrap justify-content-between">
                    <a
                      href="#"
                      className="txt-link-d"
                      onClick={(e) => {
                        e.preventDefault()
                        navigate('/dashboard')
                      }}
                    >
                      <i className="fas fa-chevron-left me-1"></i>
                      หน้าหลัก
                    </a>
                    <a
                      href="#"
                      className="txt-link-d"
                      onClick={(e) => {
                        e.preventDefault()
                        void handleRefresh()
                      }}
                    >
                      <i className={`fas ${refreshing ? 'fa-spinner fa-spin' : 'fa-sync-alt'} me-1`}></i>
                      รีเฟรช
                    </a>
                  </div>
                </div>

                <div className="col-12 px-1">
                  <div className="w-100 p-2 bgc-7">
                    <div className="bdr-5 bgc-4 c-red px-2 py-1 fs-18 t-medium mb-2">
                      <i className="fas fa-money-bill-wave me-2"></i>
                      {displayConfig.pageTitle}
                    </div>

                    <div className="bgc-4 bdr-5 p-3 mb-2 text-center">
                      <small className="c-red text-uppercase t-medium">{displayConfig.heroBadgeText}</small>
                      <h5 className="c-dark mt-2 mb-1">{displayConfig.pageTitle}</h5>
                      <p className="c-dark fs-14 mb-1">{displayConfig.pageSubtitle}</p>
                      <p className="c-red fs-14 mb-0">{displayConfig.promotionalMessage}</p>
                    </div>

                    {loading ? (
                      <div className="bgc-4 bdr-5 p-4 text-center">
                        <i className="fas fa-spinner fa-spin c-red fs-24"></i>
                        <p className="c-dark mt-2 mb-0">กำลังโหลดข้อมูล cashback...</p>
                      </div>
                    ) : error ? (
                      <div className="bgc-4 bdr-5 p-3 text-center">
                        <p className="c-red mb-2">{error}</p>
                        <button type="button" className="check-button bgc-13 c-white border-0" onClick={() => void loadCashbackData()}>
                          โหลดใหม่
                        </button>
                      </div>
                    ) : cashbackDisabled ? (
                      <div className="bgc-4 bdr-5 p-3 text-center">
                        <h5 className="c-red mb-1">{displayConfig.disabledTitle}</h5>
                        <p className="c-dark mb-0">{displayConfig.disabledMessage}</p>
                      </div>
                    ) : (
                      <>
                        <div className="row mx--1 mb-2">
                          {cards.map((item) => (
                            <div key={item.label} className="col-6 col-md-3 px-1 mb-2">
                              <div className="bgc-4 bdr-5 p-2 h-100 text-center">
                                <p className="c-dark mb-1">{item.label}</p>
                                <h5 className={`${item.className} t-medium mb-0`}>{formatCurrency(item.value)}</h5>
                              </div>
                            </div>
                          ))}
                        </div>

                        <div className="bgc-4 bdr-5 p-3 mb-2">
                          <div className="row mx--1 align-items-center">
                            <div className="col-12 col-md-7 px-1 mb-2 mb-md-0">
                              <small className="c-dark d-block mb-1">ยอด cashback ล่าสุดที่สามารถกดรับได้</small>
                              <h2 className="c-red t-medium mb-1">
                                {formatCurrency(latestClaimable?.calculation?.cashbackAmount || 0)}
                              </h2>
                              <p className="c-dark fs-14 mb-0">{displayConfig.noticeText}</p>
                              <div className="row mx--1 mt-2">
                                <div className="col-12 col-md-6 px-1 mb-2 mb-md-0">
                                  <div className="bgc-7 bdr-5 p-2 h-100">
                                    <small className="c-dark d-block mb-1">งวดที่พร้อมรับล่าสุด</small>
                                    <div className="c-red t-medium fs-14">
                                      {latestClaimable
                                        ? `${formatDateTime(latestClaimable.periodStartAt)} - ${formatDateTime(latestClaimable.periodEndAt)}`
                                        : 'ยังไม่มีรายการพร้อมรับ'}
                                    </div>
                                  </div>
                                </div>
                                <div className="col-12 col-md-6 px-1">
                                  <div className="bgc-7 bdr-5 p-2 h-100">
                                    <small className="c-dark d-block mb-1">Turnover ที่ต้องทำ</small>
                                    <div className="c-main t-medium fs-14">
                                      {latestClaimable ? `${latestClaimable.bonusLock?.turnoverMultiplier || 1}x` : '-'}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div className="col-12 col-md-5 px-1">
                              <button
                                type="button"
                                className="check-button bgg-5 c-white border-0 w-100"
                                disabled={!latestClaimable?._id || claiming}
                                onClick={() => void handleClaim()}
                              >
                                <i className={`fas ${claiming ? 'fa-spinner fa-spin' : 'fa-hand-holding-usd'} me-2`}></i>
                                {claiming ? 'กำลังทำรายการ...' : latestClaimable ? 'รับเครดิตเงินคืน' : 'ยังไม่มีรายการให้รับ'}
                              </button>
                              {claimMessage ? (
                                <div className="bgc-7 bdr-5 p-2 mt-2 text-center">
                                  <small className="c-dark">{claimMessage}</small>
                                </div>
                              ) : null}
                            </div>
                          </div>
                        </div>

                        <div className="row mx--1 mb-2">
                          <div className="col-12 col-md-6 px-1 mb-2 mb-md-0">
                            <div className="bgc-4 bdr-5 p-2 h-100">
                              <p className="c-dark mb-1">อัตราเครดิตเงินคืน</p>
                              <h5 className="c-red t-medium mb-0">{displayConfig.cashbackRateText}</h5>
                            </div>
                          </div>
                          <div className="col-12 col-md-6 px-1">
                            <div className="bgc-4 bdr-5 p-2 h-100">
                              <p className="c-dark mb-1">ถอนขั้นต่ำที่แนะนำ</p>
                              <h5 className="c-main t-medium mb-0">{formatCurrency(displayConfig.minWithdrawalAmount)}</h5>
                            </div>
                          </div>
                        </div>

                        <div className="row mx--1 mb-2">
                          {displayConfig.showRateTable && (
                            <div className={`px-1 mb-2 ${displayConfig.showExample ? 'col-12 col-lg-7' : 'col-12'}`}>
                              <div className="bgc-4 bdr-5 p-2 h-100">
                                <div className="wrap justify-content-between align-items-center mb-2">
                                  <div>
                                    <h6 className="c-red t-medium mb-1">เงื่อนไขและอัตราเงินคืน</h6>
                                    <small className="c-dark">รายละเอียดการรับ cashback ตามการตั้งค่าปัจจุบันของระบบ</small>
                                  </div>
                                  <div className="bgg-1 bdr-5 px-2 py-1 c-white t-medium">
                                    {displayConfig.cashbackRateText}
                                  </div>
                                </div>

                                <div className="row mx--1">
                                  {tableRows.map((row) => (
                                    <div key={row.label} className="col-12 px-1 mb-2">
                                      <div className="row mx--1">
                                        <div className="col-12 col-md-5 px-1 mb-1 mb-md-0">
                                          <div className="bgc-7 bdr-5 p-2 h-100 c-dark t-medium">
                                            {row.label}
                                          </div>
                                        </div>
                                        <div className="col-12 col-md-7 px-1">
                                          <div className="bgc-7 bdr-5 p-2 h-100 c-red">
                                            {row.value || '-'}
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                </div>

                                <div className="bdr-5 p-2 mt-2" style={{ background: 'rgba(0, 161, 111, 0.08)', border: '1px solid rgba(0, 161, 111, 0.16)' }}>
                                  <small className="c-red t-medium d-block mb-1">หมายเหตุ</small>
                                  <p className="c-dark fs-14 mb-0">{displayConfig.noticeText}</p>
                                </div>
                              </div>
                            </div>
                          )}

                          {displayConfig.showExample && (
                            <div className={`px-1 mb-2 ${displayConfig.showRateTable ? 'col-12 col-lg-5' : 'col-12'}`}>
                              <div className="bgc-4 bdr-5 p-2 h-100">
                                <h6 className="c-red t-medium mb-2">ภาพรวมการรับ cashback</h6>

                                <div className="bgc-7 bdr-5 p-2 mb-2">
                                  <h6 className="c-dark t-medium mb-2">ตัวอย่างการคำนวณ</h6>
                                  {exampleSteps.map((step) => (
                                    <p key={step} className="c-dark fs-14 mb-1">{step}</p>
                                  ))}
                                </div>

                                <div className="bgg-1 bdr-5 p-2 c-white">
                                  <small className="d-block mb-1 text-uppercase">Highlight</small>
                                  <p className="mb-0 t-medium">{displayConfig.promotionalMessage}</p>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>

                        <div className="bgc-4 bdr-5 p-2 mb-2">
                          <div className="wrap justify-content-between align-items-center mb-2">
                            <h6 className="c-red t-medium mb-0">ประวัติ cashback ล่าสุด</h6>
                            <a
                              href="#"
                              className="txt-link-d"
                              onClick={(e) => {
                                e.preventDefault()
                                navigate('/withdrawal')
                              }}
                            >
                              <i className="fas fa-wallet me-1"></i>
                              {displayConfig.withdrawalButtonText}
                            </a>
                          </div>

                          {history.length === 0 ? (
                            <div className="bgc-7 bdr-5 p-3 text-center">
                              <p className="c-dark mb-0">{displayConfig.historyEmptyText}</p>
                            </div>
                          ) : (
                            <div className="row mx--1">
                              {history.map((item) => (
                                <div key={item._id} className="col-12 px-1 mb-2">
                                  <div className="bgc-7 bdr-5 p-2">
                                    <div className="wrap justify-content-between align-items-start">
                                      <div>
                                        <p className="c-dark mb-1">
                                          งวด {formatDateTime(item.periodStartAt)} - {formatDateTime(item.periodEndAt)}
                                        </p>
                                        <small className={statusClass[item.status] || 'c-dark'}>
                                          {statusLabel[item.status] || item.status}
                                        </small>
                                      </div>
                                      <div className="text-end">
                                        <h6 className="c-red t-medium mb-1">
                                          {formatCurrency(item.calculation?.cashbackAmount || 0)}
                                        </h6>
                                        <small className="c-dark">
                                          เทิร์น {formatCurrency(item.sourceSummary?.validTurnover || 0)}
                                        </small>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default CashbackPage
